/*
 * Algo to calculate the Popup position.
 *
 * 1. center position for popup content: the center of the trigger will be the
 *    center of the content so the popup content position will be like this:
 *    - top => the y of the center for the trigger element: trigger.top + trigger.height/2
 *    - left => the x of the center for the trigger element: trigger.left + trigger.width/2
 *
 * 2. translate position according to the first position attribute passed in the function argument.
 *    For example : position = 'left top'
 *    We need to handle the first argument in the position:
 *    'left' => that's mean we need to translate the popup content according to the
 *    X axis by - content.width/2
 *
 * 3. translate position according to the first position attribute passed in
 *    the function argument. For example : position = 'left top'
 *    the second argument 'top' => translate popup content by + content.height*4/5
 *
 * 4. check if calculated position is going out of bounds of wrapper box or not. If
 *    yes, repeat 1-3 for next position enum. By default wrapper box is window element.
 */
import { POPUP_ROOT, ZOOM_400_MAX_WIDTH } from "../../common/constants";
export const POSITION_TYPES = ["top left", "top center", "top right", "right top", "right center", "right bottom", "bottom left", "bottom center", "bottom right", "left top", "left center", "left bottom"];

const getCoordinatesForPosition = _ref => {
  let {
    triggerBounding,
    contentBounding,
    position,
    arrow,
    offsetX,
    offsetY
  } = _ref;
  const margin = arrow ? 8 : 0;
  const args = position.split(" "); // the step N 1 : center the popup content => ok

  const CenterTop = triggerBounding.top + triggerBounding.height / 2;
  const CenterLeft = triggerBounding.left + triggerBounding.width / 2;
  const {
    height,
    width
  } = contentBounding;
  let top = CenterTop - height / 2,
      left = CenterLeft - width / 2,
      transform = "",
      arrowTop = "0%",
      arrowLeft = "0%"; // eslint-disable-next-line default-case

  switch (args[0]) {
    case "top":
      top -= height / 2 + triggerBounding.height / 2 + margin;
      transform = "rotate(180deg) translateX(50%)";
      arrowTop = "100%";
      arrowLeft = "50%";
      break;

    case "bottom":
      top += height / 2 + triggerBounding.height / 2 + margin;
      transform = "rotate(0deg) translateY(-100%) translateX(-50%)";
      arrowLeft = "50%";
      break;

    case "left":
      left -= width / 2 + triggerBounding.width / 2 + margin;
      transform = "rotate(90deg) translateY(50%) translateX(-25%)";
      arrowLeft = "100%";
      arrowTop = "50%";
      break;

    case "right":
      left += width / 2 + triggerBounding.width / 2 + margin;
      transform = "rotate(-90deg) translateY(-150%) translateX(25%)";
      arrowTop = "50%";
      break;
  } // eslint-disable-next-line default-case


  switch (args[1]) {
    case "top":
      top = triggerBounding.top;
      arrowTop = `${triggerBounding.height / 2}px`;
      break;

    case "bottom":
      top = triggerBounding.top - height + triggerBounding.height;
      arrowTop = `${height - triggerBounding.height / 2}px`;
      break;

    case "left":
      left = triggerBounding.left;
      arrowLeft = `${triggerBounding.width / 2}px`;
      break;

    case "right":
      left = triggerBounding.left - width + triggerBounding.width;
      arrowLeft = `${width - triggerBounding.width / 2}px`;
      break;
  }

  top = args[0] === "top" ? top - offsetY : top + offsetY;
  left = args[0] === "left" ? left - offsetX : left + offsetX;
  return {
    top,
    left,
    transform,
    arrowLeft,
    arrowTop
  };
};

export const getTooltipBoundary = (keepTooltipInside, triggerBounding, hasRootNode) => {
  // add viewport
  let boundingBox = {
    top: 0,
    left: 0,
    width: window.innerWidth,
    height: window.innerHeight
  };
  /**
   * If there is a root node, the bounding rectangle is adjusted relative
   * to it. So, initially the trigger would be at the top left of the
   * bounding rectangle and now it will be more in the center.
   * Negative top and left values will result.
   */

  if (hasRootNode) {
    boundingBox.top -= triggerBounding.top;
    boundingBox.left -= triggerBounding.left;
  }

  if (typeof keepTooltipInside === "string") {
    const selector = document.querySelector(keepTooltipInside);
    selector !== null && (boundingBox = selector.getBoundingClientRect());
  }

  return boundingBox;
};
export const calculatePosition = _ref2 => {
  let {
    previousBestCoords,
    triggerBounding,
    contentBounding,
    position,
    arrow,
    offset,
    keepTooltipInside,
    rootNode
  } = _ref2;
  let bestCoords = {
    arrowLeft: "0%",
    arrowTop: "0%",
    left: 0,
    top: 0,
    transform: "rotate(135deg)"
  },
      i = 0,
      positions = Array.isArray(position) ? position : [position],
      foundNewBestCoords = false;
  const {
    offsetX,
    offsetY
  } = offset;
  const wrapperBox = getTooltipBoundary(keepTooltipInside, triggerBounding, Boolean(rootNode));
  /**
   * If there is a root node, adjust the trigger bounding rectangle to
   * relate to it for absolute positioning.
   */

  if (rootNode) {
    const rootNodeBounding = rootNode.getBoundingClientRect();
    triggerBounding.top -= rootNodeBounding.top;
    triggerBounding.bottom = triggerBounding.top + triggerBounding.height;
    triggerBounding.left -= rootNodeBounding.left;
    triggerBounding.x = triggerBounding.left;
    triggerBounding.y = triggerBounding.top;
  }

  if (keepTooltipInside || Array.isArray(position)) {
    // Using filter() to remove duplicates.
    positions = [...positions, ...POSITION_TYPES].filter((v, i, a) => a.indexOf(v) === i);
  }
  /**
   * Looping through all positions ("top left", "top right", etc),
   * to try to find the one that best fit in the current
   * viewport.
   *
   * As a stop gap, if none are actually fitting (meaning the
   * algorithm is flawed), then if we have existing previous
   * coordinates, we re-use them.
   *
   * However, if we are in a very small viewport, likely zoom at 400%,
   * we try to force either top center or bottom center, with left
   * artificially set to 0.
   */


  while (i < positions.length) {
    bestCoords = getCoordinatesForPosition({
      triggerBounding,
      contentBounding,
      position: positions[i],
      arrow,
      offsetX,
      offsetY
    });
    const contentBox = {
      top: bestCoords.top,
      left: bestCoords.left,
      width: contentBounding.width,
      height: contentBounding.height
    };

    if (contentBox.top <= wrapperBox.top || contentBox.left <= wrapperBox.left || contentBox.top + contentBox.height >= wrapperBox.top + wrapperBox.height || contentBox.left + contentBox.width >= wrapperBox.left + wrapperBox.width) {
      // position does not fit, let's move to the next candidate
      i++;
    } else {
      foundNewBestCoords = true;
      break;
    }
  }

  if (!foundNewBestCoords && wrapperBox.width <= ZOOM_400_MAX_WIDTH) {
    /**
     * No acceptable coordinates have been found, AND the
     * screen size is less than 320 (probably a 400% scenario),
     * therefore we are going to choose either topCenter or
     * bottomCenter, with the added bonus that their .left attribute
     * is set to 0, to make sure the content of the popup fits the
     * viewport. It's not a perfect solution, but it allows the content
     * to be visible in such a small screen area.
     */
    if (triggerBounding.top - 10 < contentBounding.height) {
      const bottomCenter = getCoordinatesForPosition({
        triggerBounding,
        contentBounding,
        position: "bottom center",
        arrow,
        offsetX,
        offsetY
      });
      bottomCenter.left = 0;
      return bottomCenter;
    } else {
      const topCenter = getCoordinatesForPosition({
        triggerBounding,
        contentBounding,
        position: "top center",
        arrow,
        offsetX,
        offsetY
      });
      topCenter.left = 0;
      return topCenter;
    }
  }

  if (foundNewBestCoords || !foundNewBestCoords && !previousBestCoords) {
    return bestCoords;
  }

  return previousBestCoords ? previousBestCoords : bestCoords;
};
/**
 * Get the main parent node of the current popup.
 *
 * @returns Element
 */

export const getRootPopup = () => {
  return document.getElementById(POPUP_ROOT);
};
/**
 * Creates the main parent node if it does not
 * exist yet, and appends it to body.
 *
 * @returns Element
 */

export const createRootPopup = () => {
  let popupRoot = getRootPopup();

  if (popupRoot === null) {
    popupRoot = document.createElement("div");
    popupRoot.setAttribute("id", POPUP_ROOT);
    document.body.appendChild(popupRoot);
  }

  return popupRoot;
};
/**
 * Adds a popupId to the list of popupId in the dataset called
 * "data-popups" associated with the main parent node.
 *
 * @param {String} popupId The unique id of the current popup
 * @param {Ref} popupRootRef The React ref of the main parent node
 */

export const savePopupId = (popupId, popupRootRef) => {
  let newList;

  if (popupRootRef.current) {
    var _popupRootRef$current, _popupRootRef$current2;

    const currentList = (_popupRootRef$current = popupRootRef.current) === null || _popupRootRef$current === void 0 ? void 0 : (_popupRootRef$current2 = _popupRootRef$current.dataset) === null || _popupRootRef$current2 === void 0 ? void 0 : _popupRootRef$current2.popups;

    if (currentList) {
      newList = currentList.split(",");
      newList.push(popupId);
    } else {
      newList = popupId;
    }

    popupRootRef.current.dataset.popups = newList;
  }
};
/**
 * Removed a popupId from the list of popupId in the dataset called
 * "data-popups" associated with the main parent node.
 *
 * @param {String} popupId The unique id of the current popup
 * @param {Ref} popupRootRef The React ref of the main parent node
 */

export const removePopupId = (popupId, popupRootRef) => {
  var _popupRootRef$current3, _popupRootRef$current4;

  const currentList = popupRootRef !== null && popupRootRef !== void 0 && (_popupRootRef$current3 = popupRootRef.current) !== null && _popupRootRef$current3 !== void 0 && (_popupRootRef$current4 = _popupRootRef$current3.dataset) !== null && _popupRootRef$current4 !== void 0 && _popupRootRef$current4.popups ? popupRootRef.current.dataset.popups.split(",") : [];
  const indexToRemove = currentList.indexOf(popupId);

  if (indexToRemove >= 0) {
    currentList.splice(indexToRemove, 1);
    popupRootRef.current.dataset.popups = currentList;
    return true;
  }

  return false;
};
/**
 * Extract the most recent popupId from the list of popupId in
 * the dataset called "data-popups" associated with the main
 * parent node.
 *
 * @param {Ref} popupRootRef The React ref of the main parent node
 */

export const getMostRecentPopupId = popupRootRef => {
  var _popupRootRef$current5, _popupRootRef$current6;

  const listOfPopups = popupRootRef !== null && popupRootRef !== void 0 && (_popupRootRef$current5 = popupRootRef.current) !== null && _popupRootRef$current5 !== void 0 && (_popupRootRef$current6 = _popupRootRef$current5.dataset) !== null && _popupRootRef$current6 !== void 0 && _popupRootRef$current6.popups ? popupRootRef.current.dataset.popups.split(",") : [];

  if (listOfPopups.length) {
    return listOfPopups[listOfPopups.length - 1];
  }

  return false;
};
/**
 * Returns an object with DomRect properties for cases when these need to
 * be modified.
 * @param {*} element HTML element
 * @returns Object Object with DomRect properties.
 */

export const getBoundingClientRect = element => {
  const {
    bottom,
    height,
    left,
    right,
    top,
    width,
    x,
    y
  } = element.getBoundingClientRect();
  return {
    bottom,
    height,
    left,
    right,
    top,
    width,
    x,
    y
  };
};